import alt from 'components/Dispatcher';
import immutableStore from 'alt-utils/lib/ImmutableUtil';
import {Map, List, Record} from 'immutable';
import UserActions from 'actions/UserActions';
import {UserSourceAsyncStoreConfig} from 'sources/UserSourceAsyncStoreConfig';
import User, {UserId} from 'records/User';
import {associateBy} from 'utils/associateBy';
import {Store} from '@store/@types/alt';

type RepId = UserId;
class UserStoreState extends Record({
  // a repository of all users that have been requested so far
  // Map<UserId, User>
  users: Map(),
  // more granular maps of which users are loading and which
  // have seen error responses
  usersLoading: Map(),
  usersErrored: Map(),
  // the list users returned from the most recent request
  userList: List(),
  // big catch alls to signify that there are users loading
  // or there has been an error when loading users..
  // not intended for use going forward
  userListLoading: false,
  userListError: false,
  // Manager specific maps
  // These are key'd by their subordinate rep's ids
  // Otherwise they behave similarly to the user versions
  managers: Map(),
  managersLoading: Map(),
  managersErrored: Map(),
}) {
  // a repository of all users that have been requested so far
  // Map<UserId, User>
  users: Map<UserId, User>;
  // more granular maps of which users are loading and which
  // have seen error responses
  usersLoading: Map<UserId, boolean>;
  usersErrored: Map<UserId, boolean>;
  // the list users returned from the most recent request
  userList: List<User>;
  // big catch alls to signify that there are users loading
  // or there has been an error when loading users..
  // not intended for use going forward
  userListLoading: boolean;
  userListError: boolean;
  // Manager specific maps
  // These are key'd by their subordinate rep's ids
  // Otherwise they behave similarly to the user versions
  managers: Map<RepId, User>;
  managersLoading: Map<RepId, boolean>;
  managersErrored: Map<RepId, boolean>;
}

/**
 * A `UserStore` is a client-side global singleton that manages all the users and their metadata
 * on the client-side. On the client-side, it's the single-source-of-truth for all user related data.
 * Its `users` is an Immutable.Map object which maps `User.id` to `User` objects
 */
@immutableStore
class UserStore extends Store<UserStoreState> {
  constructor() {
    super();
    this.state = new UserStoreState();
    this.bindActions(UserActions);
    this.registerAsync(UserSourceAsyncStoreConfig);
  }

  /**
   * Update `this.state.users` (an Immutable.Map) using `users` (an array)
   * @param  {Object[]} users An array of `User` objects
   */
  updateUsers(users) {
    let state = this.state;
    users.forEach(user => {
      state = state.setIn(['users', user.id], new User(user));
    });
    this.setState(state);
  }

  /**
   * Fetch users using `UserSource.fetchUsers`
   */
  onFetchUsers(args) {
    args = args || [];
    let userIds = args[0];
    if (typeof userIds === 'undefined') {
      userIds = [];
    }
    this.getInstance<typeof UserSourceAsyncStoreConfig>().fetch(userIds);
    this.preventDefault();
  }

  onFetchSuccess(resp) {
    this.fetchAllSuccess(resp);
  }

  fetchAll(params) {
    this.setState(
      this.state.merge({
        userListLoading: true,
        userListError: false,
        usersLoading:
          params && params.ids
            ? params.ids
                .filter(id => !!id)
                .reduce(
                  (usersLoading, userId) => usersLoading.set(userId, true),
                  this.state.usersLoading
                )
            : this.state.usersLoading,
        usersErrored:
          params && params.ids
            ? params.ids.reduce(
                (usersErrored, userId) => usersErrored.set(userId, false),
                this.state.usersErrored
              )
            : this.state.usersErrored,
      })
    );
  }

  fetchAllSuccess(userList) {
    // "isTeamManager" doesnt return from all of our endpoints.
    // if any endpoint ever returned if, for a particular user, isTeamManager,
    // preserve that. Note the caching implication, however, until a page refresh
    const coalesceTeamManagers = userList.map(u => {
      const oldUser = this.state.users.get(u.id);

      return u.set('isTeamManager', u.isTeamManager || (oldUser && oldUser.isTeamManager));
    });

    this.setState(
      this.state.merge({
        userListLoading: false,
        userList: coalesceTeamManagers,
        users: associateBy(coalesceTeamManagers, u => u.id, this.state.users),
        usersLoading: userList.reduce(
          (usersLoading, user) => usersLoading.set(user.id, false),
          this.state.usersLoading
        ),
      })
    );
  }

  fetchAllError(args) {
    const error = Array.isArray(args) ? args[1] : args;
    const params = args?.[0];
    console.error(error);
    console.error(params);
    this.setState(
      this.state.merge({
        userListLoading: false,
        userListError: true,
        usersLoading:
          params && params.ids
            ? params.ids.reduce(
                (usersLoading, userId) => usersLoading.set(userId, false),
                this.state.usersLoading
              )
            : this.state.usersLoading,
        usersErrored:
          params && params.ids
            ? params.ids.reduce(
                (usersErrored, userId) => usersErrored.set(userId, true),
                this.state.usersErrored
              )
            : this.state.usersErrored,
      })
    );
  }

  fetchManager({rep_id}) {
    this.setState(
      this.state.merge({
        managersLoading: this.state.managersLoading.set(rep_id, true),
      })
    );
  }

  fetchManagerSuccess([manager, {rep_id}]) {
    this.setState(
      this.state.merge({
        users: this.state.users.set(manager.id, manager),
        managers: this.state.managers.set(rep_id, manager),
        managersLoading: this.state.managersLoading.set(rep_id, false),
      })
    );
  }

  fetchManagerError([{rep_id}, error]) {
    this.setState(
      this.state.merge({
        managersLoading: this.state.managersLoading.set(rep_id, false),
        managersErrored: this.state.managersErrored.set(rep_id, true),
      })
    );
  }

  uploadAvatarSuccess([userId, avatarUrl]) {
    this.setState(
      this.state.merge({
        users: this.state.users.setIn([userId, 'avatar'], avatarUrl),
      })
    );
  }

  uploadAvatarError(error) {
    console.error(error);
  }
}

export default alt.createStore(UserStore);
